import React from 'react';

const flavorNotes = [
  { name: 'Chocolate', intensity: 4, note: 'Smooth cocoa, the dominant note' },
  { name: 'Cinnamon', intensity: 3, note: 'Warm spice on the finish' },
  { name: 'Nutty', intensity: 2, note: 'Almond underneath' },
  { name: 'Sweet', intensity: 2, note: 'Balanced, not sharp' }
];

const profile = [
  { dimension: 'Caffeine', level: 5, label: 'Very high' },
  { dimension: 'Body', level: 3, label: 'Medium' },
  { dimension: 'Acidity', level: 1, label: 'Mild' },
  { dimension: 'Crema', level: 5, label: 'Excellent' }
];

function Bar({ value, max = 5, color = 'bg-clay' }) {
  return (
    <div className="flex gap-1">
      <span className="sr-only">{value} of {max}</span>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`h-1.5 w-4 sm:w-6 rounded-sm ${i < value ? color : 'bg-gray-300'}`}
        />
      ))}
    </div>
  );
}

export default function TasteAndBrewing() {
  return (
    <section id="taste-profile" className="relative py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">

          {/* Section Header — left aligned, not centered */}
          <div className="max-w-3xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Taste Profile</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Chocolate first, spice after.
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              A balanced cup with low acidity and a thick crema. The chocolate carries the front, cinnamon arrives in the finish, and the body sits where espresso wants it.
            </p>
          </div>

          {/* Two-column data: flavor notes / profile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20">
            <div>
              <h3 className="font-heading text-2xl lg:text-3xl text-gray-900 mb-6">Flavor notes</h3>
              <dl className="divide-y divide-gray-300/70 border-y border-gray-300/70">
                {flavorNotes.map((n) => (
                  <div key={n.name} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 sm:gap-4 py-5">
                    <dt className="w-24 sm:w-28 flex-shrink-0 font-heading text-lg text-clay">{n.name}</dt>
                    <dd className="order-last basis-full sm:order-none sm:basis-auto sm:flex-1 min-w-0 text-gray-700 text-sm">{n.note}</dd>
                    <dd className="ml-auto sm:ml-0 flex-shrink-0"><Bar value={n.intensity} /></dd>
                  </div>
                ))}
              </dl>
            </div>

            <div>
              <h3 className="font-heading text-2xl lg:text-3xl text-gray-900 mb-6">Cup profile</h3>
              <dl className="divide-y divide-gray-300/70 border-y border-gray-300/70">
                {profile.map((p) => (
                  <div key={p.dimension} className="flex items-center gap-4 py-5">
                    <dt className="w-24 sm:w-28 flex-shrink-0 font-heading text-lg text-clay">{p.dimension}</dt>
                    <dd className="flex-1 min-w-0 text-gray-700 text-sm">{p.label}</dd>
                    <dd className="flex-shrink-0"><Bar value={p.level} /></dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Brewing methods — recommended one is larger */}
          <div>
            <h3 className="font-heading text-2xl lg:text-3xl text-gray-900 mb-2">How to brew it</h3>
            <p className="text-gray-700 mb-8 max-w-2xl">Optimised for pressure brewing. Versatile enough for drip, but the crema only shows up under pressure.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              <div className="md:col-span-2 lg:col-span-6 bg-gray-900 text-white p-8 lg:p-10 rounded-3xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-400 mb-4">Recommended</p>
                <h4 className="font-heading text-3xl lg:text-4xl mb-3">Espresso</h4>
                <p className="text-gray-300 leading-relaxed">9 bars, 25 second shot, 1:2 ratio. The thick crema is the giveaway that this is the harvest it was tuned for.</p>
              </div>
              <div className="lg:col-span-3 bg-white border border-gray-200 p-6 lg:p-8 rounded-3xl">
                <h4 className="font-heading text-xl text-clay mb-2">Moka pot</h4>
                <p className="text-sm text-gray-700">Strong, concentrated. Closer to espresso than drip.</p>
              </div>
              <div className="lg:col-span-3 bg-white border border-gray-200 p-6 lg:p-8 rounded-3xl">
                <h4 className="font-heading text-xl text-clay mb-2">Pour over</h4>
                <p className="text-sm text-gray-700">Clean cup. Recipe: 13g coffee, 230g water, V60.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
